import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Loader2, Phone, Mail, Building2, MapPin, DollarSign,
  CalendarDays, Briefcase, Plus, MessageSquare, PhoneCall, Calendar as CalIcon,
  Mail as MailIcon, FileText, StickyNote, Repeat, UserPlus, PackagePlus, CheckCircle2,
  Trophy, Archive, RotateCcw,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { CRM_SUITE_OPTIONS, suiteLabel } from '@/lib/crmSuites';
import LocalizedDateInput from '@/components/shared/LocalizedDateInput';
import RequestClientCreationDialog from '@/components/admin/RequestClientCreationDialog';
import RequestSuiteActivationDialog from '@/components/admin/RequestSuiteActivationDialog';

const PIPELINE_STATUSES = [
  'lead', 'contacted', 'meeting_scheduled', 'proposal_sent',
  'negotiation', 'won', 'lost',
];
const ACTIVITY_TYPES = ['call', 'meeting', 'email', 'note', 'proposal', 'follow_up', 'internal_comment'];
const ACT_ICON = {
  call: PhoneCall, meeting: CalIcon, email: MailIcon, note: StickyNote,
  proposal: FileText, follow_up: Repeat, internal_comment: MessageSquare,
};

const fmtMoney = (n) => n == null ? '—' :
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

/**
 * Iter 41.36 — Commercial CRM lead detail.
 *
 * - hm_admin can reassign advisor + change every field.
 * - hm_advisor can edit content fields but the advisor select is hidden.
 * - 404 if the lead isn't in the caller's scope (advisor trying to view
 *   another advisor's record).
 */
export default function AdminCommercialLeadDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';

  const [data, setData] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [newActivity, setNewActivity] = useState({ activity_type: 'note', notes: '' });
  const [addingActivity, setAddingActivity] = useState(false);

  // Iter 41.37 — request dialogs
  const [rccOpen, setRccOpen] = useState(false);
  const [rsaOpen, setRsaOpen] = useState(false);

  // Global Archive sprint
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const archiveLead = async () => {
    setArchiving(true);
    try {
      await adminApi.archiveEntity('commercial_lead', id);
      toast.success(t('pages.crm.archive.archived', { defaultValue: 'Opportunity archived' }));
      setArchiveOpen(false);
      reload();
    } catch (e) {
      toast.error(t('pages.crm.archive.failed', { defaultValue: 'Could not archive' }), { description: formatApiError(e) });
    } finally { setArchiving(false); }
  };

  const restoreLead = async () => {
    setArchiving(true);
    try {
      await adminApi.restoreEntity('commercial_lead', id);
      toast.success(t('pages.crm.archive.restored', { defaultValue: 'Opportunity restored' }));
      reload();
    } catch (e) {
      toast.error(t('pages.crm.archive.restoreFailed', { defaultValue: 'Could not restore' }), { description: formatApiError(e) });
    } finally { setArchiving(false); }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const d = await adminApi.getCommercialLead(id);
      setData(d);
      setForm({ ...d.lead });
      if (isAdmin && advisors.length === 0) {
        try { setAdvisors(await adminApi.listAdvisors()); } catch { /* non-fatal */ }
      }
    } catch (e) {
      toast.error(t('pages.crm.detail.loadFailed', { defaultValue: 'Failed to load lead' }), {
        description: formatApiError(e),
      });
      navigate('/admin/crm');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      ['company_name', 'primary_contact_name', 'job_title', 'country', 'industry',
       'company_size', 'phone', 'email', 'pipeline_status', 'estimated_value',
       'next_action', 'follow_up_date', 'lead_source', 'notes', 'interested_suites',
      ].forEach((k) => { if (form[k] !== data.lead[k]) patch[k] = form[k]; });
      if (isAdmin && form.assigned_advisor_id !== data.lead.assigned_advisor_id) {
        patch.assigned_advisor_id = form.assigned_advisor_id || null;
      }
      if (Object.keys(patch).length === 0) { setEditing(false); setSaving(false); return; }
      if (patch.estimated_value !== undefined && patch.estimated_value !== null && patch.estimated_value !== '') {
        patch.estimated_value = Number(patch.estimated_value);
      }
      await adminApi.patchCommercialLead(id, patch);
      toast.success(t('pages.crm.detail.saved', { defaultValue: 'Lead updated' }));
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(t('pages.crm.detail.saveFailed', { defaultValue: 'Could not save' }), {
        description: formatApiError(e),
      });
    } finally { setSaving(false); }
  };

  const addActivity = async () => {
    if (!newActivity.notes.trim()) return;
    setAddingActivity(true);
    try {
      await adminApi.addCommercialActivity(id, newActivity);
      setNewActivity({ activity_type: 'note', notes: '' });
      reload();
    } catch (e) {
      toast.error(t('pages.crm.detail.activityFailed', { defaultValue: 'Could not add activity' }), {
        description: formatApiError(e),
      });
    } finally { setAddingActivity(false); }
  };

  if (loading) {
    return (
      <>
        <Topbar title={t('pages.crm.detail.title', { defaultValue: 'Lead detail' })} />
        <div className="p-6 lg:p-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }
  if (!data) return null;
  const { lead, activities } = data;
  const advisorName = advisors.find((a) => a.id === lead.assigned_advisor_id)?.name;

  return (
    <>
      <Topbar title={lead.company_name} subtitle={t('pages.crm.detail.title', { defaultValue: 'Lead detail' })} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="crm-lead-detail">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/crm')} className="-ml-2" data-testid="crm-back">
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('pages.crm.backToBoard', { defaultValue: 'Back to pipeline' })}
        </Button>

        {/* Won Handoff (Founder-approved) + Iter 41.37 action strip */}
        {(() => {
          const isArchived = lead.archive_state?.archived === true;
          const isWon = lead.pipeline_status === 'won';
          const canRequestClient = ['negotiation', 'won'].includes(lead.pipeline_status) && !lead.linked_company_id && !isArchived;
          const canRequestSuite  = !!lead.linked_company_id && !isArchived;
          const canArchive = ['won', 'lost'].includes(lead.pipeline_status) && !isArchived;
          return (
            <>
              {isArchived && (
                <Card className="p-4 shadow-card border-slate-300 bg-slate-50 flex flex-wrap items-center gap-3"
                      data-testid="crm-archived-banner">
                  <Archive className="h-5 w-5 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{t('pages.crm.archive.archivedBadge', { defaultValue: 'Archived opportunity' })}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('pages.crm.archive.archivedNote', { defaultValue: 'This record is hidden from active views. History and audit trail are preserved.' })}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button variant="outline" onClick={restoreLead} disabled={archiving} data-testid="crm-restore-btn">
                      <RotateCcw className="h-4 w-4 mr-1" />
                      {t('pages.crm.archive.restore', { defaultValue: 'Restore' })}
                    </Button>
                  )}
                </Card>
              )}
              {isWon && canRequestClient && (
                <Card className="p-5 shadow-card border-emerald-300 bg-emerald-50/70" data-testid="crm-won-banner">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <Trophy className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <p className="font-semibold text-emerald-900">
                        {t('pages.crm.won.bannerTitle', { defaultValue: 'Opportunity won — next step' })}
                      </p>
                      <p className="text-sm text-emerald-800/90 mt-0.5">
                        {t('pages.crm.won.bannerBody', { defaultValue: 'Request client creation to hand this account over. The request is pre-filled from this opportunity and requires HM Admin approval — no client is created automatically.' })}
                      </p>
                    </div>
                    <Button onClick={() => setRccOpen(true)} data-testid="crm-won-cta"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <UserPlus className="h-4 w-4 mr-1.5" />
                      {t('pages.crm.won.cta', { defaultValue: 'Request client creation' })}
                    </Button>
                  </div>
                </Card>
              )}
              {(!isArchived && (canRequestClient || canRequestSuite || canArchive || !lead.linked_company_id)) && (
                <Card className="p-4 shadow-card flex flex-wrap items-center gap-3 border-primary/20"
                      data-testid="crm-actions-strip">
                  {lead.linked_company_id ? (
                    <div className="text-sm flex items-center gap-2 text-success" data-testid="crm-linked-company">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>
                        {t('pages.crm.linkedToClient', {
                          defaultValue: 'Linked to client',
                        })}
                      </span>
                      <Button variant="link" size="sm" className="px-1 h-auto"
                              onClick={() => navigate(`/admin/clients/${lead.linked_company_id}`)}
                              data-testid="crm-linked-client-link">
                        {t('pages.crm.openClient', { defaultValue: 'Open client →' })}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      {canRequestClient
                        ? t('pages.crm.requestClient.helper', {
                            defaultValue: 'Move this opportunity to active-client status.',
                          })
                        : t('pages.crm.requestClient.unavailable', {
                            defaultValue: 'Move the lead to Negotiation or Won to request client creation.',
                          })}
                    </div>
                  )}
                  <div className="ml-auto flex flex-wrap gap-2">
                    {canRequestClient && !isWon && (
                      <Button onClick={() => setRccOpen(true)} data-testid="crm-request-client-btn"
                              className="bg-gradient-primary hover:opacity-90">
                        <UserPlus className="h-4 w-4 mr-1" />
                        {t('pages.crm.requestClient.button', { defaultValue: 'Request Client Creation' })}
                      </Button>
                    )}
                    {canRequestSuite && (
                      <Button variant="outline" onClick={() => setRsaOpen(true)} data-testid="crm-request-suite-btn">
                        <PackagePlus className="h-4 w-4 mr-1" />
                        {t('pages.crm.requestSuite.button', { defaultValue: 'Request Suite Activation' })}
                      </Button>
                    )}
                    {canArchive && (
                      <Button variant="outline" onClick={() => setArchiveOpen(true)} data-testid="crm-archive-btn">
                        <Archive className="h-4 w-4 mr-1" />
                        {t('pages.crm.archive.action', { defaultValue: 'Archive' })}
                      </Button>
                    )}
                  </div>
                </Card>
              )}
            </>
          );
        })()}

        {/* Archive confirmation — Archive ≠ Delete */}
        <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
          <DialogContent className="sm:max-w-md" data-testid="crm-archive-dialog">
            <DialogHeader>
              <DialogTitle>{t('pages.crm.archive.confirmTitle', { defaultValue: 'Archive this opportunity?' })}</DialogTitle>
              <DialogDescription asChild>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mt-2">
                  <li>{t('pages.crm.archive.confirmBody1', { defaultValue: 'It will be removed from the active pipeline views.' })}</li>
                  <li>{t('pages.crm.archive.confirmBody2', { defaultValue: 'It is NOT permanently deleted — full history and audit trail are preserved.' })}</li>
                  <li>{t('pages.crm.archive.confirmBody3', { defaultValue: 'An HM Admin can restore it at any time.' })}</li>
                </ul>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setArchiveOpen(false)} data-testid="crm-archive-cancel">
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={archiveLead} disabled={archiving} data-testid="crm-archive-confirm">
                {archiving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Archive className="h-4 w-4 mr-1" />}
                {t('pages.crm.archive.confirm', { defaultValue: 'Archive' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RequestClientCreationDialog
          open={rccOpen} onOpenChange={setRccOpen} lead={lead}
          onSubmitted={() => reload()}
        />
        <RequestSuiteActivationDialog
          open={rsaOpen} onOpenChange={setRsaOpen} lead={lead}
          onSubmitted={() => reload()}
        />

        {/* Lead fields */}
        <Card className="p-6 shadow-card" data-testid="crm-lead-card">
          <div className="flex justify-between items-start gap-4 mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold">{lead.company_name}</h2>
              <p className="text-sm text-muted-foreground">{lead.primary_contact_name}{lead.job_title ? ` · ${lead.job_title}` : ''}</p>
              <Badge className="mt-2" variant="outline" data-testid="crm-status-badge">
                {t(`pages.crm.status.${lead.pipeline_status}`)}
              </Badge>
            </div>
            <div className="flex gap-2">
              {!editing
                ? <Button onClick={() => setEditing(true)} data-testid="crm-edit-btn">{t('common.edit', { defaultValue: 'Edit' })}</Button>
                : <>
                    <Button variant="ghost" onClick={() => { setForm({ ...lead }); setEditing(false); }} data-testid="crm-cancel-btn">{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                    <Button onClick={save} disabled={saving} className="bg-gradient-primary hover:opacity-90" data-testid="crm-save-btn">
                      {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.saving')}</> : t('common.save', { defaultValue: 'Save' })}
                    </Button>
                  </>}
            </div>
          </div>

          {!editing ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <Info icon={Mail}        label={t('pages.crm.field.email')}          value={lead.email} />
              <Info icon={Phone}       label={t('pages.crm.field.phone')}          value={lead.phone} />
              <Info icon={MapPin}      label={t('pages.crm.field.country')}        value={lead.country} />
              <Info icon={Building2}   label={t('pages.crm.field.industry')}       value={lead.industry} />
              <Info icon={Building2}   label={t('pages.crm.field.companySize')}    value={lead.company_size} />
              <Info icon={DollarSign}  label={t('pages.crm.field.estimatedValue')} value={fmtMoney(lead.estimated_value)} />
              <Info icon={CalendarDays}label={t('pages.crm.field.followUp')}       value={lead.follow_up_date} />
              <Info icon={Briefcase}   label={t('pages.crm.field.source')}         value={lead.lead_source ? t(`pages.crm.source.${lead.lead_source}`, { defaultValue: lead.lead_source }) : '—'} />
              {isAdmin && <Info icon={Briefcase} label={t('pages.crm.field.advisor')} value={advisorName || t('pages.crm.unassigned', { defaultValue: 'Unassigned' })} />}
              <div className="sm:col-span-2 lg:col-span-3" data-testid="crm-suites-display">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  {t('pages.crm.field.interestedSuites')}
                </p>
                <div className="flex flex-wrap gap-1">
                  {(lead.interested_suites || []).map((s) => <Badge key={s} variant="outline">{suiteLabel(s)}</Badge>)}
                  {(!lead.interested_suites || lead.interested_suites.length === 0) && <span className="text-xs text-muted-foreground">—</span>}
                </div>
              </div>
              {lead.next_action && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{t('pages.crm.field.nextAction')}</p>
                  <p className="text-sm">{lead.next_action}</p>
                </div>
              )}
              {lead.notes && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{t('pages.crm.field.notes')}</p>
                  <p className="text-sm whitespace-pre-line">{lead.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t('pages.crm.field.companyName')} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="ce-company" />
              <Field label={t('pages.crm.field.primaryContact')} value={form.primary_contact_name} onChange={(v) => setForm({ ...form, primary_contact_name: v })} testid="ce-contact" />
              <Field label={t('pages.crm.field.email')} type="email" value={form.email || ''} onChange={(v) => setForm({ ...form, email: v })} testid="ce-email" />
              <Field label={t('pages.crm.field.phone')} value={form.phone || ''} onChange={(v) => setForm({ ...form, phone: v })} testid="ce-phone" />
              <Field label={t('pages.crm.field.country')} value={form.country || ''} onChange={(v) => setForm({ ...form, country: v })} testid="ce-country" />
              <Field label={t('pages.crm.field.industry')} value={form.industry || ''} onChange={(v) => setForm({ ...form, industry: v })} testid="ce-industry" />
              <Field label={t('pages.crm.field.estimatedValue')} type="number" value={form.estimated_value || ''} onChange={(v) => setForm({ ...form, estimated_value: v })} testid="ce-value" />
              <div>
                <Label>{t('pages.crm.field.followUp')}</Label>
                <LocalizedDateInput value={form.follow_up_date || ''} onChange={(v) => setForm({ ...form, follow_up_date: v })} data-testid="ce-followup" />
              </div>
              <div>
                <Label>{t('pages.crm.field.status', { defaultValue: 'Pipeline status' })}</Label>
                <Select value={form.pipeline_status} onValueChange={(v) => setForm({ ...form, pipeline_status: v })}>
                  <SelectTrigger data-testid="ce-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PIPELINE_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`pages.crm.status.${s}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div>
                  <Label>{t('pages.crm.field.advisor')}</Label>
                  <Select value={form.assigned_advisor_id || 'unassigned'}
                          onValueChange={(v) => setForm({ ...form, assigned_advisor_id: v === 'unassigned' ? '' : v })}>
                    <SelectTrigger data-testid="ce-advisor"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">{t('pages.crm.unassigned')}</SelectItem>
                      {advisors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="sm:col-span-2">
                <Label>{t('pages.crm.field.interestedSuites')}</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="ce-suites">
                  {CRM_SUITE_OPTIONS.map((s) => {
                    const sel = (form.interested_suites || []).includes(s);
                    return (
                      <button key={s} type="button"
                              onClick={() => setForm((f) => ({
                                ...f,
                                interested_suites: (f.interested_suites || []).includes(s)
                                  ? (f.interested_suites || []).filter((x) => x !== s)
                                  : [...(f.interested_suites || []), s],
                              }))}
                              className={`px-2.5 py-1 rounded-full text-xs border transition ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-foreground border-border hover:bg-muted'}`}
                              data-testid={`ce-suite-${s}`}>
                        {suiteLabel(s)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>{t('pages.crm.field.nextAction')}</Label>
                <Input value={form.next_action || ''} onChange={(e) => setForm({ ...form, next_action: e.target.value })} data-testid="ce-next" />
              </div>
              <div className="sm:col-span-2">
                <Label>{t('pages.crm.field.notes')}</Label>
                <Textarea rows={3} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ce-notes" />
              </div>
            </div>
          )}
        </Card>

        {/* Ownership trail (admin only) */}
        {isAdmin && (
          <Card className="p-4 shadow-card text-xs text-muted-foreground" data-testid="crm-ownership">
            <p>
              {t('pages.crm.createdBy', { name: lead.created_by_name || '—', date: lead.created_at ? new Date(lead.created_at).toLocaleString(getUiLocale()) : '—',
                                          defaultValue: 'Created by {{name}} · {{date}}' })}
            </p>
            <p>
              {t('pages.crm.updatedBy', { name: lead.updated_by_name || '—', date: lead.updated_at ? new Date(lead.updated_at).toLocaleString(getUiLocale()) : '—',
                                          defaultValue: 'Last updated by {{name}} · {{date}}' })}
            </p>
          </Card>
        )}

        {/* Activity timeline */}
        <Card className="p-6 shadow-card" data-testid="crm-activities-card">
          <h3 className="font-semibold mb-3">{t('pages.crm.activityTimeline', { defaultValue: 'Activity timeline' })}</h3>
          <div className="grid sm:grid-cols-[180px_1fr_auto] gap-3 items-start mb-4" data-testid="crm-activity-add">
            <Select value={newActivity.activity_type} onValueChange={(v) => setNewActivity({ ...newActivity, activity_type: v })}>
              <SelectTrigger data-testid="crm-activity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map((typ) => <SelectItem key={typ} value={typ}>{t(`pages.crm.activity.${typ}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea rows={2} value={newActivity.notes}
                      onChange={(e) => setNewActivity({ ...newActivity, notes: e.target.value })}
                      placeholder={t('pages.crm.activity.notesPh', { defaultValue: 'Add a note…' })}
                      data-testid="crm-activity-notes" />
            <Button onClick={addActivity} disabled={addingActivity || !newActivity.notes.trim()}
                    className="bg-gradient-primary hover:opacity-90" data-testid="crm-activity-add-btn">
              {addingActivity
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t('common.saving')}</>
                : <><Plus className="h-4 w-4 mr-1" /> {t('pages.crm.addActivity', { defaultValue: 'Add' })}</>}
            </Button>
          </div>
          <div className="space-y-3" data-testid="crm-timeline">
            {activities.length === 0 ? (
              <p className="text-xs text-muted-foreground italic text-center py-4">
                {t('pages.crm.noActivities', { defaultValue: 'No activities yet.' })}
              </p>
            ) : activities.map((a) => {
              const isStage = a.activity_type === 'stage_change';
              const Icon = isStage ? Repeat : (ACT_ICON[a.activity_type] || StickyNote);
              return (
                <div key={a.id} className="flex gap-3 pb-3 border-b last:border-0" data-testid={`crm-activity-row-${a.id}`}>
                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold">{t(`pages.crm.activity.${a.activity_type}`, { defaultValue: isStage ? 'Stage change' : a.activity_type })}</span>
                      <span className="text-xs text-muted-foreground">{a.user_name || '—'}</span>
                      <span className="text-xs text-muted-foreground">· {new Date(a.timestamp).toLocaleString(getUiLocale())}</span>
                    </div>
                    {isStage ? (
                      <p className="text-sm mt-0.5" data-testid={`crm-stage-change-${a.id}`}>
                        {t('pages.crm.activity.stageChangedLine', {
                          from: t(`pages.crm.status.${a.stage_from}`, { defaultValue: a.stage_from || '—' }),
                          to: t(`pages.crm.status.${a.stage_to}`, { defaultValue: a.stage_to || '—' }),
                          defaultValue: 'Stage changed: {{from}} → {{to}}',
                        })}
                      </p>
                    ) : (
                      <p className="text-sm whitespace-pre-line mt-0.5">{a.notes}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </p>
      <p className="text-sm">{value || '—'}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
